// Small deterministic numeric helpers for the ski v3 engine. No randomness, no clock.

export const MEMBER_Z: readonly number[] = [-1.5, -1, -0.65, -0.3, 0, 0.3, 0.65, 1, 1.5];
export const MEMBER_WEIGHTS: readonly number[] = [0.04, 0.08, 0.12, 0.16, 0.2, 0.16, 0.12, 0.08, 0.04];
export const MEMBER_COUNT = MEMBER_Z.length;

export const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
export const clamp = (n: number, lo = 0, hi = 1): number => (n < lo ? lo : n > hi ? hi : n);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** Logistic sigmoid of x/width about centre: sigma((x - c)/w). */
export const sigma = (x: number): number => (x >= 0 ? 1 / (1 + Math.exp(-x)) : Math.exp(x) / (1 + Math.exp(x)));
export const logistic = (x: number, centre: number, width: number): number => sigma((x - centre) / width);
/** Cubic smoothstep on t clamped to [0, 1]; derivative 0 at both ends. */
export const smoothstep = (t: number): number => {
  const u = clamp(t);
  return u * u * (3 - 2 * u);
};

/** Standard normal CDF (Abramowitz-Stegun 7.1.26 via erf), accurate to ~1e-7. */
export function normalCdf(x: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(x) / Math.SQRT2);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-(x * x) / 2);
  return x >= 0 ? 0.5 * (1 + y) : 0.5 * (1 - y);
}

/** Inverse standard normal CDF (Acklam), relative error ~1e-9. */
export function normalInv(p: number): number {
  const q = clamp(p, 1e-12, 1 - 1e-12);
  const a = [-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1, 2.506628277459239];
  const b = [-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1];
  const c = [-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const lo = 0.02425;
  if (q < lo) {
    const s = Math.sqrt(-2 * Math.log(q));
    return (((((c[0] * s + c[1]) * s + c[2]) * s + c[3]) * s + c[4]) * s + c[5]) / ((((d[0] * s + d[1]) * s + d[2]) * s + d[3]) * s + 1);
  }
  if (q > 1 - lo) {
    const s = Math.sqrt(-2 * Math.log(1 - q));
    return -(((((c[0] * s + c[1]) * s + c[2]) * s + c[3]) * s + c[4]) * s + c[5]) / ((((d[0] * s + d[1]) * s + d[2]) * s + d[3]) * s + 1);
  }
  const s = q - 0.5;
  const r = s * s;
  return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * s / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}

export function normalPdf(x: number): number {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

export function weightedMean(values: readonly number[], weights: readonly number[]): number {
  let s = 0;
  let w = 0;
  for (let i = 0; i < values.length; i++) {
    const wi = weights[i] ?? 0;
    if (!finite(values[i]) || !(wi > 0)) continue;
    s += values[i] * wi;
    w += wi;
  }
  return w > 0 ? s / w : NaN;
}

/**
 * Weighted quantile with mid-point plotting positions and linear interpolation.
 * Values need not be sorted. Returns NaN for empty input.
 */
export function weightedQuantile(values: readonly number[], weights: readonly number[], q: number): number {
  const pairs = values.map((v, i) => [v, weights[i] ?? 0] as const).filter(([v, w]) => finite(v) && w > 0).sort((a, b) => a[0] - b[0]);
  if (!pairs.length) return NaN;
  const total = pairs.reduce((s, [, w]) => s + w, 0);
  let cum = 0;
  const positions = pairs.map(([, w]) => {
    const mid = (cum + w / 2) / total;
    cum += w;
    return mid;
  });
  const target = clamp(q);
  if (target <= positions[0]) return pairs[0][0];
  if (target >= positions[positions.length - 1]) return pairs[pairs.length - 1][0];
  for (let i = 1; i < positions.length; i++) {
    if (target <= positions[i]) {
      const t = (target - positions[i - 1]) / Math.max(1e-12, positions[i] - positions[i - 1]);
      return lerp(pairs[i - 1][0], pairs[i][0], t);
    }
  }
  return pairs[pairs.length - 1][0];
}

/** Power-mean soft minimum, p > 0 (larger p -> closer to min). Values floored at `floor`. */
export function softMin(values: readonly number[], weights: readonly number[], p = 6, floor = 0.001): number {
  let s = 0;
  let w = 0;
  for (let i = 0; i < values.length; i++) {
    const wi = weights[i] ?? 0;
    if (!(wi > 0) || !finite(values[i])) continue;
    s += wi * Math.pow(Math.max(values[i], floor), -p);
    w += wi;
  }
  return w > 0 ? Math.pow(s / w, -1 / p) : NaN;
}

/** Log-sum-exp soft maximum: (1/beta) ln( sum w e^(beta v) / sum w ). */
export function softMax(values: readonly number[], weights: readonly number[], beta = 0.05): number {
  const pairs = values.map((v, i) => [v, weights[i] ?? 0] as const).filter(([v, w]) => finite(v) && w > 0);
  if (!pairs.length) return NaN;
  const max = Math.max(...pairs.map(([v]) => v));
  const total = pairs.reduce((s, [, w]) => s + w, 0);
  const sum = pairs.reduce((s, [v, w]) => s + w * Math.exp(beta * (v - max)), 0);
  return max + Math.log(sum / total) / beta;
}

/** Deterministic 32-bit FNV-1a hash as hex, for cache and terrain keys. */
export function stableHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

export const round1 = (n: number): number => Math.round(n * 10) / 10;

// Pure SHA-256 (FIPS 180-4) over a UTF-8 string, hex output. Deterministic, no I/O,
// usable in any runtime. Used for the engine's inputSetHash.
const SHA256_K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function utf8Bytes(text: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < text.length; i++) {
    let c = text.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) { c = 0x10000 + ((c - 0xd800) << 10) + (next - 0xdc00); i++; }
    }
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

export function sha256Hex(text: string): string {
  const bytes = utf8Bytes(text);
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(padded.length - 4, bitLength >>> 0);
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const w = new Uint32Array(64);
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = ((w[i - 15] >>> 7) | (w[i - 15] << 25)) ^ ((w[i - 15] >>> 18) | (w[i - 15] << 14)) ^ (w[i - 15] >>> 3);
      const s1 = ((w[i - 2] >>> 17) | (w[i - 2] << 15)) ^ ((w[i - 2] >>> 19) | (w[i - 2] << 13)) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + SHA256_K[i] + w[i]) >>> 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + hh) >>> 0;
  }
  return Array.from(h, v => v.toString(16).padStart(8, "0")).join("");
}

/** Canonical JSON: object keys sorted, numbers as JSON, undefined dropped. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(v => canonicalJson(v === undefined ? null : v)).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
}
